import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, CornerDownLeft, Receipt, Search } from "lucide-react";
import { SECTIONS, pathOf, sectionOf } from "./sections";

const REFERENCE = /^\s*MAG-([0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12})\s*$/i;

// ⌘K / Ctrl+K / "/" search: sections by name, and orders by their full MAG reference.
export default function CommandPalette({ open, onClose, modules }) {
  const nav = useNavigate(),
    input = useRef(null),
    returnFocus = useRef(null),
    [query, setQuery] = useState(""),
    [index, setIndex] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    setQuery("");
    setIndex(0);
    const t = setTimeout(() => input.current?.focus(), 0);
    return () => { clearTimeout(t); returnFocus.current?.focus?.(); };
  }, [open]);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase(), out = [];
    const ref = REFERENCE.exec(query);
    if (ref) out.push({ key: "order", label: `Open order MAG-${ref[1].toUpperCase()}`, hint: "Order", to: `/admin/orders/${ref[1].toLowerCase()}`, icon: Receipt });
    for (const [key, label] of modules) {
      const section = sectionOf(key);
      if (!q || label.toLowerCase().includes(q) || section?.label.toLowerCase().includes(q))
        out.push({ key, label, hint: section?.label || "", to: pathOf(key), icon: section?.icon || SECTIONS[0].icon, tint: section?.tint });
    }
    return out.slice(0, 12);
  }, [query, modules]);
  if (!open) return null;
  const go = (r) => { onClose(); nav(r.to); };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && results[index]) { e.preventDefault(); go(results[index]); }
  };
  return (
    <div className="ios-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ios-sheet aw-palette" role="dialog" aria-modal="true" aria-label="Search the workspace" onKeyDown={onKey}>
        <label className="aw-palette-input">
          <Search size={18} aria-hidden="true" />
          <span className="aw-sr">Search sections or paste an order reference</span>
          <input
            ref={input}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setIndex(0); }}
            placeholder="Search sections or paste an order reference"
            role="combobox"
            aria-expanded="true"
            aria-controls="aw-palette-results"
            aria-activedescendant={results[index] ? `aw-palette-${results[index].key}` : undefined}
          />
          <kbd>esc</kbd>
        </label>
        <ul id="aw-palette-results" className="aw-palette-results" role="listbox">
          {results.length === 0 && <li className="aw-palette-empty">No section matches “{query}”. Order references look like MAG-1A2B3C4D-…</li>}
          {results.map((r, i) => (
            <li key={r.key} id={`aw-palette-${r.key}`} role="option" aria-selected={i === index}>
              <button type="button" className="ios-row" onMouseEnter={() => setIndex(i)} onClick={() => go(r)}>
                <span className="ios-tile" data-tint={r.tint || "gold"}><r.icon aria-hidden="true" /></span>
                <span className="ios-row-main"><span className="ios-row-title">{r.label}</span>{r.hint && <span className="ios-row-sub">{r.hint}</span>}</span>
                {i === index ? <CornerDownLeft size={15} className="ios-chevron" aria-hidden="true" /> : <ChevronRight size={15} className="ios-chevron" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
